/**
 * ──────────────────────────────────────────────────────────
 * @module  public/js/services/contactsStagingEmailFinder.js
 * @summary Helper to look up a contact’s email from Firestore staging by CRM Contact ID.
 *
 * @description
 * Queries the `/ContactsStaging` collection in Firestore for a document whose
 * `crmContactId` field matches the given HubSpot/CRM contact ID, then returns
 * its `email` field (populated by the staging worker).
 *
 * ## Public API
 * – `lookupStagedEmail(crmContactId: string): Promise<string>`
 *
 * ## Dependencies
 * – Firebase Firestore (v9 modular)
 *
 * @author
 * @version 1.0.0
 * @since   2025-05-11
 * ──────────────────────────────────────────────────────────
 */

import {
  getFirestore,
  getDoc,
  query,
  where,
  getDocs,
  limit,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { firebaseApp } from '../core/firebase-service.js';
import { colWithTenant, docWithTenant } from '../utils/firestoreHelpers.js';
import { resolveRuntimeDi } from '../utils/runtimeDi.js';

const db = getFirestore(firebaseApp);

/**
 * Return the first non-empty email address from a Firestore query snapshot.
 *
 * Some Focus Mode edit flows may create additional staging rows for the same
 * contact id without carrying forward the original `email` field. This helper
 * intentionally skips those partial rows and keeps searching for a record that
 * can actually power the email composer.
 *
 * @param {{ docs?: Array<{ data: () => Record<string, any> }> }} snapshot - Firestore query snapshot.
 * @returns {string} First trimmed email address, or an empty string.
 */
function pickEmailFromSnapshot(snapshot) {
  const docs = Array.isArray(snapshot?.docs) ? snapshot.docs : [];
  for (const docSnap of docs) {
    const email = String(docSnap?.data?.()?.email || '').trim();
    if (email) return email;
  }
  return '';
}

/**
 * Find the staging document whose `crmContactId` equals the given ID,
 * and return its `email` field.
 *
 * @param {string} crmContactId – the HubSpot/CRM contact ID
 * @param {Record<string, unknown>} [services] - Optional DI overrides.
 * @returns {Promise<string>} the email address from the staging record
 * @throws {Error} if no matching record is found or if the record lacks an `email` field
 */
export async function lookupStagedEmail(crmContactId, services = null) {
  const di = resolveRuntimeDi(services);
  const user = di.auth?.currentUser ?? (await di.auth?.waitForLogin?.());
  const tenantId = user?.tenantId;
  if (!tenantId) {
    console.error('Missing tenant ID');
    throw new Error('Missing tenant ID');
  }

  const directSnap = await getDoc(
    docWithTenant(tenantId, `ContactsStaging/${crmContactId}`)
  );
  if (directSnap.exists()) {
    const directEmail = String(directSnap.data()?.email || '').trim();
    if (directEmail) {
      return directEmail;
    }
  }

  const col = colWithTenant(tenantId, 'ContactsStaging');
  let q = query(col, where('crmContactId', '==', crmContactId), limit(5));
  let snap = await getDocs(q);
  let email = pickEmailFromSnapshot(snap);
  if (email) {
    return email;
  }
  if (snap.empty) {
    q = query(col, where('contactId', '==', crmContactId), limit(5));
    snap = await getDocs(q);
    email = pickEmailFromSnapshot(snap);
    if (email) {
      return email;
    }
  }

  if (snap.empty) {
    throw new Error(
      `No staging record found for CRM Contact ID ${crmContactId}`
    );
  }

  const data = snap.docs[0].data();
  if (!data.email) {
    throw new Error(
      `Staging record for CRM Contact ID ${crmContactId} has no email field`
    );
  }

  return String(data.email).trim();
}
